import React, { useState, useCallback } from "react"
import { gql, useMutation, useQuery } from "@apollo/client"

import { CardHeading, Fieldset } from "components"
import { Modal } from "components"
import { Tabs, TabPanel, useTab } from "hooks/useTabs"

import { Popover } from "@headlessui/react"
import { Formik, Form, FieldArray } from "formik"

import * as yup from "yup"

import formatMoney from "utils/formatMoney"
import groupBy from "lodash/groupBy"
import sortBy from "lodash/sortBy"
import uniq from "lodash/uniq"

import { toast } from "react-hot-toast"

import {
  sortableContainer,
  sortableElement,
  sortableHandle,
} from "react-sortable-hoc"
import arrayMove from "array-move"

const PRODUCTS = gql`
  query Trip($id: ID!) {
    trip(id: $id) {
      productDeposits {
        id
        category
        amount
        dueDate
      }

      products {
        id
        name
        description
        category
        price
        availability
        quantity
        index
      }

      settings {
        creditCardFee
      }
    }
  }
`

const CREATE_PRODUCTS = gql`
  mutation CreateProducts($input: CreateProductsInput!) {
    createProducts(input: $input) {
      id
      name
      description
      category
      price
      availability
      quantity
      index
    }
  }
`

const UPDATE_PRODUCTS = gql`
  mutation UpdateProducts($input: UpdateProductsInput!) {
    updateProducts(input: $input) {
      id
      name
      description
      category
      price
      availability
      quantity
      index
    }
  }
`

const DELETE_PRODUCTS = gql`
  mutation DeleteProducts($input: DeleteProductsInput!) {
    deleteProducts(input: $input) {
      id
    }
  }
`

const CREATE_DEPOSITS = gql`
  mutation CreateTripDeposits($input: CreateTripDepositsInput!) {
    createTripDeposits(input: $input) {
      id
      category
      amount
      dueDate
    }
  }
`

const UPDATE_DEPOSITS = gql`
  mutation UpdateTripDeposits($input: UpdateTripDepositsInput!) {
    updateTripDeposits(input: $input) {
      id
      category
      amount
      dueDate
    }
  }
`

const DELETE_DEPOSITS = gql`
  mutation DeleteTripDeposits($input: DeleteTripDepositsInput!) {
    deleteTripDeposits(input: $input) {
      id
    }
  }
`

const ProductsContainer = ({ tripId }) => {
  const { data, loading, error, refetch } = useQuery(PRODUCTS, {
    variables: { id: tripId },
  })
  const [createProducts] = useMutation(CREATE_PRODUCTS)
  const [updateProducts] = useMutation(UPDATE_PRODUCTS)
  const [deleteProducts] = useMutation(DELETE_PRODUCTS)

  const [createTripDeposits] = useMutation(CREATE_DEPOSITS)
  const [updateTripDeposits] = useMutation(UPDATE_DEPOSITS)
  const [deleteTripDeposits] = useMutation(DELETE_DEPOSITS)

  const handleCreate = async (products) => {
    const loadingId = toast.loading("Saving...")
    try {
      const input = { tripId, products }
      const res = await createProducts({
        variables: { input },
        update(cache, { data }) {
          const existing = cache.readQuery({
            query: PRODUCTS,
            variables: { id: tripId },
          })

          const existingProducts = existing?.trip?.products

          cache.writeQuery({
            query: PRODUCTS,
            variables: { id: tripId },
            data: {
              trip: {
                ...existing?.trip,
                products: [
                  ...(existingProducts ? existingProducts : []),
                  ...data?.createProducts,
                ],
              },
            },
          })
        },
      })
      toast.dismiss(loadingId)
      toast.success("Products saved!")

      return res
    } catch (error) {
      toast.dismiss(loadingId)
      toast.error("Could not save.")
      throw error
    }
  }

  const handleUpdate = async (products) => {
    const loadingId = toast.loading("Saving...")
    try {
      const input = { tripId, products }

      const res = await updateProducts({
        variables: { input },
        update(cache, { data }) {
          const existing = cache.readQuery({
            query: PRODUCTS,
            variables: { id: tripId },
          })

          const products = existing?.trip?.products
          const updateProducts = data?.updateProducts

          cache.writeQuery({
            query: PRODUCTS,
            variables: { id: tripId },
            data: {
              trip: {
                ...existing?.trip,
                products: [
                  ...products.map((item) => {
                    const isUpdate = updateProducts.find(
                      (_) => _.id === item.id,
                    )
                    return isUpdate ? isUpdate : item
                  }),
                ],
              },
            },
          })
        },
      })
      toast.dismiss(loadingId)
      toast.success("Products saved!")
      return res
    } catch (error) {
      toast.dismiss(loadingId)
      toast.error("Could not save.")
      throw error
    }
  }

  const handleDelete = async (products) => {
    try {
      const input = { tripId, products }

      await deleteProducts({
        variables: { input },
        update(cache, { data }) {
          const existing = cache.readQuery({
            query: PRODUCTS,
            variables: { id: tripId },
          })

          const products = existing?.trip?.products
          const deletedProductIds = data?.deleteProducts.map((_) => _.id)

          cache.writeQuery({
            query: PRODUCTS,
            variables: { id: tripId },
            data: {
              trip: {
                ...existing?.trip,
                products: products.filter(
                  (_) => !deletedProductIds.includes(_?.id),
                ),
              },
            },
          })
        },
      })
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  const handleDepositsCreate = async (deposits) => {
    const loadingId = toast.loading("Saving...")
    try {
      const input = { tripId, deposits }
      const res = await createTripDeposits({ variables: { input } })
      toast.dismiss(loadingId)
      toast.success("Deposits saved!")
      await refetch()
      return res
    } catch (error) {
      toast.dismiss(loadingId)
      toast.error("Could not save.")
      throw error
    }
  }

  const handleDepositsUpdate = async (deposits) => {
    const loadingId = toast.loading("Saving...")
    try {
      const input = { tripId, deposits }
      const res = await updateTripDeposits({ variables: { input } })
      toast.dismiss(loadingId)
      toast.success("Products updated!")
      await refetch()
      return res
    } catch (error) {
      toast.dismiss(loadingId)
      toast.error("Could not save.")
      throw error
    }
  }

  const handleDepositsDelete = async (deposits) => {
    const loadingId = toast.loading("Saving...")
    try {
      const input = { tripId, deposits }
      const res = await deleteTripDeposits({ variables: { input } })
      toast.dismiss(loadingId)
      toast.success("Deposits deleted!")
      await refetch()
      return res
    } catch (error) {
      toast.dismiss(loadingId)
      toast.error("Could not save.")
      throw error
    }
  }

  const [isEditing, setIsEditing] = useState(false)

  const openModal = useCallback(async () => {
    setIsEditing(true)
  }, [setIsEditing])

  const closeModal = useCallback(async () => {
    setIsEditing(false)
  }, [setIsEditing])

  const isEmpty = data?.trip?.products?.length === 0

  const products = sortBy(data?.trip?.products, "index")

  const deposits = data?.trip?.productDeposits

  return (
    <div className="bg-white rounded-lg shadow-lg">
      {!data || isEmpty ? (
        <>
          <CardHeading title="Products" />
          <ProductEmpty isLoading={loading} onClick={openModal} />
        </>
      ) : (
        <>
          <CardHeading title="Products" ctaTitle="Edit" onClick={openModal} />
          <ProductList data={products} />
        </>
      )}

      <Modal title="Products" isOpen={isEditing} onClose={closeModal}>
        <ProductsForm
          data={{ products, deposits }}
          settings={data?.trip?.settings}
          onClose={closeModal}
          onCreate={handleCreate}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          onCreateDeposits={handleDepositsCreate}
          onUpdateDeposits={handleDepositsUpdate}
          onDeleteDeposits={handleDepositsDelete}
        />
      </Modal>
    </div>
  )
}

export default ProductsContainer

const ProductEmpty = ({ isLoading, onClick }) => {
  return (
    <div className="py-16 flex flex-col justify-center items-center">
      {isLoading ? (
        <div>...Loading...</div>
      ) : (
        <>
          {" "}
          <p className="text-center">No Products</p>
          <button
            onClick={onClick}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="-ml-1 mr-2 h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            Add Products
          </button>
        </>
      )}
    </div>
  )
}

const ProductList = ({ data }) => {
  const productsByCategory = groupBy(data, (_) => _.category)
  const categories = Object.keys(productsByCategory)

  return (
    <Tabs>
      <ul className="flex items-center border-b border-gray-300">
        {categories?.map((category, index) => (
          <ProductCategoryTab
            name={category}
            key={index}
            id={index}
            variant="regular"
          />
        ))}
      </ul>

      {categories?.map((category, index) => (
        <TabPanel key={index} id={index}>
          <ProductTable data={productsByCategory[category]} />
        </TabPanel>
      ))}
    </Tabs>
  )
}

const ProductCategoryTab = ({ name, tabKey }) => {
  const { isActive, onClick } = useTab(tabKey)

  return (
    <li className="relative flex">
      <button
        type="button"
        className={`group inline-flex items-center px-6 py-4 border-b-2 font-medium text-sm leading-5 cursor-pointer whitespace-no-wrap ${
          isActive
            ? "border-indigo-500 text-indigo-600 focus:outline-none focus:text-indigo-800 focus:border-indigo-700"
            : "border-transparent text-gray-500 hover:text-gray-600 hover:border-gray-300 focus:text-gray-600 focus:border-gray-300"
        }`}
        onClick={onClick}
      >
        {name}
      </button>
    </li>
  )
}

const ProductTable = ({ data, onSelect }) => {
  if (!data) return null

  return (
    <table className="min-w-full table-auto">
      <thead>
        <tr>
          <th
            className={`flex-1 px-6 py-3 border-b border-gray-200 bg-gray-100 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider`}
          >
            Product
          </th>
          <th
            className={`w-4/12 px-6 py-3 border-b border-gray-200 bg-gray-100 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider`}
          >
            <div className="w-full flex text-center">
              <span className="w-4/12">Initial</span>
              <span className="w-4/12">On Stock</span>
              <span className="w-4/12">Sold</span>
            </div>
            {/* On Stock/Sold */}
          </th>
          <th
            className={`w-2/12 text-right px-6 py-3 border-b border-gray-200 bg-gray-100 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider`}
          >
            Price
          </th>
        </tr>
      </thead>
      <tbody className="bg-white">
        {data?.map((product, index) => (
          <tr key={index}>
            <td className="flex-1 px-6 py-4 whitespace-no-wrap border-b border-gray-200">
              <p className="text-base leading-5 font-medium text-gray-900">
                {product.name}
              </p>

              <p className="text-sm leading-5 text-gray-500">
                {product.description}
              </p>
            </td>

            <td className="w-4/12 px-6 py-4 whitespace-no-wrap border-b border-gray-200">
              <div className="w-full flex text-center text-base leading-5 font-medium text-gray-900">
                <p className="w-4/12">
                  <span className="bg-purple-200 text-purple-800 rounded-full px-3 p-1">
                    {product?.quantity !== null ? product?.quantity : "Unlim."}
                  </span>
                </p>
                <p className="w-4/12">
                  <span className="bg-yellow-200 text-yellow-800 rounded-full px-3 p-1">
                    {product?.quantity !== null
                      ? product?.availability
                      : "Unlim."}
                  </span>
                </p>
                <p className="w-4/12">
                  <span className="bg-green-200 text-green-800 rounded-full px-3 p-1">
                    {product?.quantity
                      ? product?.quantity - product?.availability
                      : Math.abs(product?.availability)}
                  </span>
                </p>
              </div>

              {/* <p className="text-base leading-5 font-medium text-gray-900">
                {product?.quantity ? product?.quantity : "Unlim."} /{" "}
                {product?.quantity ? product?.availability : "Unlim."} /{" "}
                {product?.quantity
                  ? product?.quantity - product?.availability
                  : Math.abs(product?.availability)}
              </p> */}
            </td>

            <td className="w-2/12 text-right px-6 py-4 whitespace-no-wrap border-b border-gray-200 text-sm leading-5 text-gray-500">
              <p className="text-base leading-5 font-medium text-gray-900">
                {formatMoney(product?.price)}
              </p>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

//
const ModalNavigation = ({ onClose, isSubmitting }) => {
  return (
    <div className=" px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
      <span className="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
        <button
          disabled={isSubmitting}
          type="submit"
          className="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-green-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-green-500 focus:outline-none focus:border-green-700 focus:shadow-outline-green transition ease-in-out duration-150 sm:text-sm sm:leading-5 disabled:opacity-50 disabled:cursor-wait"
        >
          Save
        </button>
      </span>

      <span className="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
        <button
          onClick={onClose}
          type="button"
          className="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition ease-in-out duration-150 sm:text-sm sm:leading-5"
        >
          Cancel
        </button>
      </span>
    </div>
  )
}

const ProductsForm = ({
  data,
  settings,
  onClose,
  //
  onCreate,
  onUpdate,
  onDelete,
  //
  onCreateDeposits,
  onUpdateDeposits,
  onDeleteDeposits,
}) => {
  const productsModel = {
    name: "",
    description: "",
    category: "Lodging",
    quantity: null,
    price: 0.0,
  }

  const productsSchema = yup.object().shape({
    products: yup
      .array()
      .required("You must have at least one product")
      .min(1, "You must have at least one product")
      .of(
        yup.object({
          name: yup.string().required("Name is required"),
          // description: yup.string().required("Description is required"),
          category: yup.string().required("required"),
          // quantity: yup.number().required("required").min(1),
          price: yup.number().required("required").min(0),
        }),
      ),
  })

  const formikProps = {
    initialValues: {
      products: [
        ...(data?.products?.length > 0 ? data?.products : [productsModel]),
      ],
      deposits: [
        ...(data?.deposits && data?.deposits.length > 0
          ? data?.deposits.map((part) => ({
              ...part,
              dueDate: part?.dueDate?.split("T", 1)[0],
              category: "Air",
            }))
          : [
              { category: "Air", dueDate: "", amount: 0 },
              { category: "Air", dueDate: "", amount: 0 },
            ]),
      ],
    },
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema: productsSchema,
    onSubmit: async (values, actions) => {
      try {
        //This is expensive because everytime this functions gets called update will happen
        //TODO detect if values chaged, and then update shallowDeep(inital, current)

        const hasAir = values?.products?.some((_) => _.category === "Air")
        const initialDepositIds = data?.deposits?.map((_) => _?.id)
        const currentDepositIds = values?.deposits?.map((_) => _?.id)

        const depositsSorted = values?.deposits.map((deposit, index) => {
          return {
            ...deposit,
            dueDate:
              typeof deposit.dueDate === "boolean" ? null : deposit.dueDate,
            // index,
          }
        })

        const depositsForCreate = depositsSorted.filter((_) => !_?.id)
        const depositsForUpdate = depositsSorted.filter(
          (_) => _?.id && initialDepositIds?.includes(_?.id),
        )
        const depositsForDelete = data?.deposits?.filter(
          (_) => _?.id && !currentDepositIds?.includes(_?.id),
        )

        // return
        if (hasAir && depositsForCreate?.length > 0) {
          await onCreateDeposits(depositsForCreate)
        }

        if (hasAir && depositsForUpdate?.length > 0) {
          await onUpdateDeposits(depositsForUpdate)
        }

        // if (hasAir && depositsForUpdate?.length > 0) {
        //   await onDeleteDeposits(depositsForDelete)
        // }

        // MAP PRODUCTS
        const initialDataIds = data?.products?.map((_) => _.id)
        const currentDataIds = values?.products?.map((_) => _.id)

        const productsSorted = values?.products.map((_, index) => {
          const { ...product } = _

          const availability =
            _.availability === "" ? null : Number(_.availability)
          const quantity = _.quantity === "" ? null : Number(_.quantity)

          return {
            ...product,
            availability,
            quantity,
            index,
          }
        })

        const productsForCreate = productsSorted.filter((_) => !_.id)
        const productsForUpdate = productsSorted.filter(
          (_) => _.id && initialDataIds?.includes(_.id),
        )
        const productsForDelete = data?.products?.filter(
          (_) => _.id && !currentDataIds?.includes(_.id),
        )

        if (productsForCreate?.length > 0) {
          await onCreate(productsForCreate)
        }

        if (productsForUpdate?.length > 0) {
          await onUpdate(productsForUpdate)
        }

        if (productsForDelete?.length > 0) {
          await onDelete(productsForDelete)
        }

        onClose()
      } catch (e) {
        console.error("UPDATE TRIP INFROMATION ERROR", e)
        // const errors = e.graphQLErrors.map(v => v)
        // setFieldError("server", errors)
      }
    },
  }

  const [selectedCategory, setSelectedCategory] = useState("Lodging")

  return (
    <Formik {...formikProps}>
      {({ values, errors, setFieldValue, isSubmitting }) => {
        /**
         * on category move
         * reoder all product that contain that category
         * on add product, add to the index
         */

        const productsWithIndex = values?.products.map((product, index) => ({
          ...product,
          index,
        }))

        const productsToCategories = values?.products.map((_) => _.category)

        const categories = uniq(productsToCategories)

        const firstIndexOfCategory = (category) => {
          const index = productsToCategories.indexOf(category)
          return index === -1 ? productsToCategories.length : index + 1
        }

        const lastIndexOfCategory = (category) => {
          const index = productsToCategories.lastIndexOf(category)
          return index === -1 ? productsToCategories.length : index + 1
        }

        const handleCreateCategory = (arrayHelpers) => (category) => {
          arrayHelpers.push({ ...productsModel, category })
          setSelectedCategory(category)
        }

        const handleCategoryDelete = (category) => {
          try {
            const products = values?.products?.filter(
              (_) => _.category !== category,
            )
            setFieldValue("products", products)
          } catch (error) {
            console.error(error)
          }
        }

        const handleCategoryRename = (oldName, newName) => {
          setFieldValue(
            "products",
            values.products.map((product) => ({
              ...product,
              category:
                product.category === oldName ? newName : product.category,
            })),
          )
        }

        const handleCreateProduct = (arrayHelpers) => (category) => {
          const index = lastIndexOfCategory(category)
          arrayHelpers.insert(index, { ...productsModel, category })
        }

        const onCategorySortEnd = ({ oldIndex, newIndex, ...rest }) => {
          const category = categories[oldIndex]
          // const secondCategory = categories[newIndex]
          const deleteStart = productsToCategories.indexOf(category)
          const deleteEnd = productsToCategories.lastIndexOf(category) + 1

          const categoryToSort = productsWithIndex.slice(deleteStart, deleteEnd)

          const allProducts = productsWithIndex.filter((_, index) =>
            index >= deleteStart && index <= deleteEnd ? false : true,
          )

          const final = [...allProducts]

          final.splice(newIndex, 0, ...categoryToSort)

          setFieldValue("products", final)
        }

        const onProductSortEnd = ({ oldIndex, newIndex }) => {
          setFieldValue(
            "products",
            arrayMove(productsWithIndex, oldIndex, newIndex),
          )
        }

        return (
          <Form>
            <FieldArray
              name="products"
              render={(arrayHelpers) => (
                <div>
                  <SortableContainer
                    axis="xy"
                    onSortEnd={onCategorySortEnd}
                    useDragHandle
                  >
                    <ul className="flex items-center space-x-4 border-b border-gray-300">
                      {categories?.map((category, index) => (
                        <CategoryTab
                          // id={index}
                          key={`category-${category}`}
                          index={index}
                          name={category}
                          isActive={category === selectedCategory}
                          onClick={() => setSelectedCategory(category)}
                          isDeletable={category !== "Lodging"}
                          onDelete={() => handleCategoryDelete(category)}
                          onChange={(value) =>
                            handleCategoryRename(category, value)
                          }
                        />
                      ))}
                      <AddCategoryButton
                        createdCategories={categories}
                        onClick={handleCreateCategory(arrayHelpers)}
                      />
                    </ul>
                  </SortableContainer>

                  {/* <div className="flex-1"></div> */}

                  <header className="mt-2 flex  py-3 border-b border-gray-200 bg-gray-100 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                    <div className="w-3/12 px-3 pl-10">
                      <p>Name</p>
                    </div>

                    <div className="w-4/12 px-3">
                      <p>Description</p>
                    </div>

                    <div className="w-1/12 px-3">
                      <p>Q.</p>
                    </div>

                    <div className="w-3/12 px-3 flex  space-x-2">
                      {settings.creditCardFee > 0 && (
                        <p className="flex-1">Price</p>
                      )}
                      <p className="flex-1">Price+CCFee</p>
                    </div>
                    {/* 

                    <div className="w-1/12"></div> */}
                  </header>

                  {/* 3,4,1,3,1 */}
                  <div>
                    <SortableContainer
                      onSortEnd={onProductSortEnd}
                      useDragHandle
                    >
                      {productsWithIndex.map((product, index) =>
                        product.category === selectedCategory ? (
                          <FieldProduct
                            key={index}
                            index={index}
                            _index={index}
                            {...product}
                            settings={settings}
                            onDelete={() => arrayHelpers.remove(product.index)}
                          />
                        ) : null,
                      )}
                    </SortableContainer>

                    {selectedCategory === "Air" && (
                      <div className="w-full px-2 py-3">
                        <label className="block text-sm font-medium text-gray-700">
                          Payment schedule:
                        </label>
                        <header className="mt-2 flex justify-between items-end px-6 py-3 border-b border-gray-200 bg-gray-100 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                          <div className="w-3/12">
                            <p>#</p>
                          </div>

                          <div className="w-3/12 ">
                            <p>Amount</p>
                          </div>

                          <div className="w-3/12">
                            <p>Due date</p>
                          </div>
                        </header>

                        {values.deposits?.map((_, index) => (
                          <>
                            <div
                              key={index}
                              className="flex justify-between items-end my-4 px-6"
                            >
                              <div className="w-3/12 text-base pb-3 capitalize">
                                <label>
                                  {index !== values.deposits.length - 1
                                    ? `Deposit`
                                    : "Final payment"}
                                </label>
                              </div>

                              <div className="w-3/12 self-center">
                                {index !== values.deposits.length - 1 ? (
                                  <Fieldset
                                    name={`deposits[${index}].amount`}
                                    type="number"
                                  />
                                ) : (
                                  <Fieldset
                                    listen={[
                                      `products[${
                                        lastIndexOfCategory("Air") - 1
                                      }].price`,
                                      "deposits[0].amount",
                                    ]}
                                    name={`deposits[1].amount`}
                                    component="computed"
                                    mapComputedToChange={([
                                      product,
                                      deposit,
                                    ]) => {
                                      return product - deposit
                                    }}
                                    render={({ value }) => (
                                      <p>
                                        {value > 0 ? formatMoney(value) : ""}
                                      </p>
                                    )}
                                  />
                                )}
                              </div>

                              <div className="w-3/12 flex justify-between items-center">
                                <div className="w-6/12">
                                  <Fieldset
                                    name={`deposits[${index}].dueDate`}
                                    label="At Regis."
                                    component="switch"
                                    // min={TODAY}
                                    // max={endDate}
                                  />
                                </div>

                                {values?.deposits[index].dueDate ===
                                true ? null : (
                                  <Fieldset
                                    name={`deposits[${index}].dueDate`}
                                    type="date"
                                    // min={TODAY}
                                    // max={endDate}
                                  />
                                )}
                              </div>
                            </div>
                          </>
                        ))}

                        {/* <ErrorMessage name="payment.deposits">
                          {(msg) =>
                            typeof msg === "string" && (
                              <div className="text-sm error bg-red-200 text-red-800 p-2 mt-2">
                                {msg}
                              </div>
                            )
                          }
                        </ErrorMessage> */}
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() =>
                        handleCreateProduct(arrayHelpers)(selectedCategory)
                      }
                      className="text-blue-500 hover:text-blue-700 py-2 px-1 my-6 rounded flex"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>

                      <span className="ml-2">Add {selectedCategory}</span>
                    </button>
                  </div>
                </div>
              )}
            />

            <ModalNavigation {...{ onClose, isSubmitting }} />
          </Form>
        )
      }}
    </Formik>
  )
}

const CategoryTab = sortableElement(
  ({
    name,

    isDeletable,
    onDelete,
    isActive,
    onClick,
    onChange,
  }) => {
    const isEditable = ![
      "Lodging",
      "Lift",
      "Rentals",
      "Transfer",
      "Air",
      "Excursions",
    ].includes(name)

    return (
      <li
        className={`w-32 resize-xrelative flex items-center pr-4 border-b-2 ${
          isActive
            ? "border-indigo-500 text-indigo-600 focus:outline-none focus:text-indigo-800 focus:border-indigo-700"
            : "border-transparent text-gray-500 hover:text-gray-600 hover:border-gray-300 focus:text-gray-600 focus:border-gray-300"
        }`}
      >
        <div>
          <DragHandle />
        </div>

        <button
          type="button"
          className={`group flex-1 items-center p-4  font-medium text-sm leading-5 cursor-pointer whitespace-no-wrap `}
          onClick={onClick}
        >
          {isEditable ? (
            <input
              className="input-inline"
              value={name}
              onChange={(e) => onChange(e.target.value)}
            />
          ) : (
            name
          )}
        </button>

        {isDeletable && (
          <button
            type="button"
            // className="rounded-full text-sm focus:outline-none focus:shadow-outline focus:bg-indigo-600 hover:bg-indigo-600 hover:text-white transition duration-300 ease-in-out"
            onClick={onDelete}
            title="Close tab"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </li>
    )
  },
)

const AddCategoryButton = ({ createdCategories, onClick }) => {
  const categories = [
    "Lodging",
    "Lift",
    "Rentals",
    "Transfer",
    "Air",
    "Excursions",
    "Other",
  ].filter((_) => !createdCategories.includes(_))

  return (
    <Popover className="relative">
      <Popover.Button type="button">
        <span className="py-2 inline-flex items-center">
          <div
            type="button"
            className="p-1 flex space-x-2 rounded-full text-sm text-gray-500 focus:outline-none focus:shadow-outline focus:bg-indigo-600 hover:bg-indigo-600 hover:text-white transition duration-300 ease-in-out"
            // onClick={onClick}
            title="New tab"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              ></path>
            </svg>
            Add Category
          </div>
        </span>
      </Popover.Button>

      <Popover.Panel className="absolute z-50 left-0">
        <div className="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
          <ul className=" flex flex-col bg-white">
            {categories.map((category, index) => (
              <button
                type="button"
                className="w-full  py-2 px-8"
                key={index}
                onClick={() => onClick(category)}
              >
                {category}
              </button>
            ))}

            {/* <a href="/analytics">Settings</a>
            <a href="/engagement">Media</a>
            <a href="/security">Logout</a> */}
          </ul>
        </div>
      </Popover.Panel>
    </Popover>
  )
}

function percentage(percent, total) {
  const value = (percent / 100) * total
  return value
}

const FieldProduct = sortableElement(
  ({ _index: index, id, category, settings, onDelete }) => {
    const [touched, setIsTouched] = useState(false)

    return (
      <div className="w-full">
        <div className="flex flex-wrap items-center">
          <div className="w-3/12 px-2 py-3 flex items-center space-x-2">
            <DragHandle />
            <Fieldset name={`products[${index}].name`} placeholder="Name" />
          </div>

          <div className="w-4/12 px-2 py-3">
            <Fieldset
              name={`products[${index}].description`}
              placeholder="Description"
            />
          </div>

          <div className="w-1/12 px-2 py-3">
            {id ? (
              <Fieldset
                name={`products[${index}].availability`}
                type="availability"
                Placeholder="∞"
              />
            ) : (
              <Fieldset
                name={`products[${index}].quantity`}
                type="quantity"
                Placeholder="∞"
              />
            )}
          </div>

          <div className="w-3/12 flex px-2 py-3 space-x-2">
            {/* {settings.creditCardFee > 0 && (
              <Fieldset name={`products[${index}].priceRaw`} type="price" />
            )} */}

            <Fieldset name={`products[${index}].price`} type="price" />

            <div className="hidden">
              {/* <Fieldset
                listen={[`products[${index}].priceRaw`,]}
                name={`products[${index}].price`}
                component="computed"
                mapComputedToChange={([priceRaw]) => {
                  return priceRaw
                    ? priceRaw + percentage(settings.creditCardFee, priceRaw)
                    : 0
                }}
                render={() => null}
              /> */}
            </div>
          </div>

          <div className="w-1/12 px-2 py-3 ">
            {index === 0 && category === "Lodging" ? null : (
              <button type="button" onClick={onDelete}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    )
  },
)

const FieldDeposits = ({ data }) => {
  return (
    <div className="w-full px-2 py-3">
      <label className="block text-sm font-medium text-gray-700">
        Payment schedule:
      </label>
      <header className="mt-2 flex justify-between items-end px-6 py-3 border-b border-gray-200 bg-gray-100 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
        <div className="w-3/12">
          <p>#</p>
        </div>

        <div className="w-3/12 ">
          <p>Amount</p>
        </div>

        <div className="w-3/12">
          <p>Due date</p>
        </div>
      </header>

      {data?.map((_, index) => (
        <>
          <div key={index} className="flex justify-between items-end my-4 px-6">
            <div className="w-3/12 text-base pb-3 capitalize">
              <label>
                {index !== data.length - 1 ? `Deposit` : "Final payment"}
              </label>
            </div>

            <div className="w-3/12  self-center">
              <Fieldset name={`deposits[${index}].amount`} type="number" />
              {/* {index !== data.length - 1 ? (
                <Fieldset name={`deposits[${index}].amount`} type="number" />
              ) : (
                <Fieldset
                  listen={[`deposits[0].amount`, "price"]}
                  name={`deposits[${index}].amount`}
                  component="computed"
                  mapComputedToChange={([amount, price]) => {
                    return price - amount
                  }}
                  render={({ value }) => (
                    <p>{value > 0 ? formatMoney(value) : ""}</p>
                  )}
                />
              )} */}
            </div>

            <div className="w-3/12 flex justify-between items-center">
              <Fieldset
                name={`deposits[${index}].dueDate`}
                type="date"
                // min={TODAY}
                // max={endDate}
              />
            </div>
          </div>
        </>
      ))}

      {/* <ErrorMessage name="payment.deposits">
{(msg) =>
  typeof msg === "string" && (
    <div className="text-sm error bg-red-200 text-red-800 p-2 mt-2">
      {msg}
    </div>
  )
}
</ErrorMessage> */}
    </div>
  )
}

const DragHandle = sortableHandle(() => (
  <div className="cursor-move">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-6 w-6"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 6h16M4 12h16M4 18h16"
      />
    </svg>
  </div>
))

const SortableItem = sortableElement(({ children }) => (
  <li>
    <DragHandle />
    {children}
  </li>
))

const SortableContainer = sortableContainer(({ children }) => {
  return <ul>{children}</ul>
})
